import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { ArrowRight, ArrowLeft, Info } from "@phosphor-icons/react";

/**
 * Password reset request page. Restricted server-side to owner + admin roles;
 * the response is intentionally generic to prevent user enumeration.
 *
 * Preview convenience: when Resend isn't configured and the target email is on
 * MFA_DEV_PREVIEW_ALLOWLIST, the backend surfaces the reset link inline as
 * `dev_preview_link`. We render it only on PREVIEW builds. On production the
 * link is silently discarded even if the backend happens to leak it — this is
 * a defence-in-depth guardrail so a mis-set env var can never expose a live
 * account's reset link on hellobyflow.com.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sent | team_member | error
  const [message, setMessage] = useState("");
  const [devLink, setDevLink] = useState("");
  const [devReason, setDevReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Verbatim product-approved copy for team-member accounts (agents,
  // supervisors, employees, owner-staff, platform-staff). Rendered on BOTH
  // the initial form and the confirmation screen so the message is
  // consistent regardless of where the user is in the flow.
  const TEAM_MEMBER_COPY =
    "Password resets are managed by your workspace administrator. Please contact your administrator to reset your password.";

  // Match Login.jsx: only preview builds can ever render the inline dev link.
  const isPreview = useMemo(() => {
    if (typeof process === "undefined" || !process.env) return false;
    if (process.env.REACT_APP_EMERGENT_PREVIEW !== "1") return false;
    if (typeof window === "undefined") return false;
    const h = (window.location?.hostname || "").toLowerCase();
    return h.includes("localhost") || h.includes("preview.emergentagent.com") || h.endsWith(".emergent.host");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.get("/auth/csrf");
      const { data } = await api.post("/auth/forgot-password", { email });
      // Team-member accounts get a distinct terminal state — NO "email
      // sent" confirmation is shown, only the "contact your admin" copy.
      if (data?.role_hint === "team_member") {
        setStatus("team_member");
        setMessage(TEAM_MEMBER_COPY);
        setDevLink("");
        setDevReason("");
        return;
      }
      setStatus("sent");
      setMessage(data?.message || "If an eligible account exists, we've emailed a reset link.");
      // Only accept the dev link on a real preview build. Production drops it.
      if (isPreview) {
        setDevLink(data?.dev_preview_link || "");
        setDevReason(data?.dev_preview_reason || "");
      } else {
        setDevLink("");
        setDevReason("");
      }
    } catch (e2) {
      setStatus("error");
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="forgot-password-page">
      <div className="w-full lg:w-1/2 flex flex-col px-12 py-10 bg-white">
        <Link to="/" className="flex items-center mb-auto">
          <Logo size={40} />
        </Link>
        <div className="max-w-sm w-full mx-auto">
          <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">/ RESET PASSWORD</div>
          <h1 className="font-display font-black text-[44px] leading-[1.0]">Forgot it?</h1>
          <p className="text-[14px] text-[var(--text-secondary)] mt-3">
            Enter the email on your Owner or Admin account. We&apos;ll email you a link to choose a new password.
          </p>

          {/* Verbatim product-approved copy — shown on the initial form so
              team members see it before they ever submit. Repeated on the
              terminal screen after submit if the email resolves to a team
              member. */}
          <div
            className="mt-4 flex items-start gap-2 rounded-lg border border-[var(--border)] bg-[#F8FAFC] px-3 py-2.5 text-[12.5px] text-[var(--text-secondary)] leading-snug"
            data-testid="forgot-team-hint"
          >
            <Info size={16} weight="fill" className="text-[var(--accent)] mt-[1px] shrink-0" />
            <div>
              <strong className="text-[var(--text-primary)]">Team member?</strong>{" "}
              {TEAM_MEMBER_COPY}
            </div>
          </div>

          {status !== "sent" && status !== "team_member" && (
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="label">Email</label>
                <input
                  className="input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="forgot-password-email"
                  autoComplete="username"
                />
              </div>
              {err && (
                <div className="text-[13px] text-[var(--danger)] font-medium" data-testid="forgot-password-error">
                  {err}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="forgot-password-submit"
              >
                {loading ? "Sending..." : <>Send reset link <ArrowRight size={16} /></>}
              </button>
            </form>
          )}

          {status === "team_member" && (
            <div className="mt-8" data-testid="forgot-password-team-member-terminal">
              {/* Dedicated terminal screen — NO "reset link sent" confirmation
                  for team members. The exact product-approved copy replaces
                  the entire success block. */}
              <div className="rounded-lg border border-[#F5D08A] bg-[#FFFAEC] p-4 text-[14px] text-[var(--text-primary)] leading-relaxed">
                {TEAM_MEMBER_COPY}
              </div>
            </div>
          )}

          {status === "sent" && (
            <div className="mt-8 space-y-4">
              <div
                className="rounded-lg border border-[var(--border)] bg-[#F5F5F0] p-4 text-[13px] text-[var(--text-primary)]"
                data-testid="forgot-password-success"
              >
                {message}
              </div>

              {devLink && (
                <div
                  className="rounded-lg border border-[#F5D08A] bg-[#FFFAEC] p-4 space-y-2"
                  data-testid="forgot-password-dev-link-box"
                >
                  <div className="font-mono text-[10px] tracking-widest text-[#8A6A00]">
                    / DEV PREVIEW · INLINE RESET LINK
                  </div>
                  <p className="text-[12px] text-[var(--text-secondary)]">
                    {devReason || "Email delivery isn't configured — link surfaced inline."}
                  </p>
                  <a
                    href={devLink}
                    className="block break-all text-[12px] text-[var(--accent)] font-mono underline"
                    data-testid="forgot-password-dev-link"
                  >
                    {devLink}
                  </a>
                </div>
              )}
            </div>
          )}

          <div className="mt-6 text-[13px]">
            <Link to="/login" className="text-[var(--accent)] font-semibold inline-flex items-center gap-1" data-testid="link-back-to-login">
              <ArrowLeft size={14} /> Back to sign in
            </Link>
          </div>
        </div>
        <div className="mt-auto font-mono text-[11px] text-[var(--text-muted)] tracking-widest">© 2026 HELLO BY FLOW</div>
      </div>

      <div className="hidden lg:flex w-1/2 bg-[var(--bg-dark)] text-white items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-[0.07]" />
        <div className="relative max-w-md">
          <div className="font-mono text-[11px] tracking-widest text-[#67E8F9] mb-4">SECURE · SINGLE-USE · 30 MIN</div>
          <h2 className="font-display font-black text-[44px] leading-[1.0]">
            One click.<br />New password.
          </h2>
          <p className="text-[15px] text-[#94A3B8] mt-5">
            Reset links are single-use and expire in 30 minutes. All active sessions are signed out when you set the new password.
          </p>
        </div>
      </div>
    </div>
  );
}
